import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../../lib/supabase';
import RuleEditor from './RuleEditor';

/**
 * Tenant-level rules section (`tenant_objective_rules` table).
 *
 * RLS allows: super_admin, tenant_admin (write); agency_owner / agency_admin
 * may have read-only access depending on policy. We attempt the write and
 * surface RLS errors if denied.
 */

// Suggested metrics — order matters (ack: spec).
export const DEFAULT_METRICS = [
  { id: 'recruited_creators',          label: 'Activity count — creators recruited per month' },
  { id: 'diamonds_total',              label: 'Volume target — total diamonds' },
  { id: 'valid_go_live_rate',          label: '% target — valid go-LIVE rate' },
  { id: 'rank_up_eligibility_7d15h',   label: '% target — rank-up eligibility 7D/15H' },
  { id: 'pk_penetration',              label: '% target — PK penetration' },
  { id: 't14_ratio',                   label: '% target — T+14 ratio' },
  { id: 'go_live_rate_15d_45h_first30',label: '% target — 15D/45H in first 30' },
  { id: 'first_30d_pk_count_20_ratio', label: '% target — 20 PK in first 30 ratio' },
  { id: 'upgrade_count',               label: 'Per-tier upgrades target' },
  { id: 'maintain_count',              label: 'Per-tier maintains target' },
];

const formatRuleSummary = (ruleType, ruleValue, t) => {
  if (!ruleType) return '—';
  switch (ruleType) {
    case 'flat':
      return t(
        'admin.objectiveRules.summary.flat',
        'Flat: {{value}}',
        { value: ruleValue?.value ?? '—' }
      );
    case 'lm_growth_pct':
      return t(
        'admin.objectiveRules.summary.lmGrowth',
        'M-1 +{{pct}}%',
        { pct: ruleValue?.pct ?? '—' }
      );
    case 'lm_value':
      return t('admin.objectiveRules.summary.lmValue', 'M-1 value (as-is)');
    case 'tier_based': {
      const count = Object.keys(ruleValue?.per_tier || {}).length;
      return t(
        'admin.objectiveRules.summary.tierBased',
        'Per-tier ({{count}} tiers)',
        { count }
      );
    }
    case 'upgrade_pct_of_tier':
      return t(
        'admin.objectiveRules.summary.upgradePctOfTier',
        '{{pct}}% upgrade per tier',
        { pct: ruleValue?.value ?? '—' }
      );
    case 'maintain_pct_of_tier':
      return t(
        'admin.objectiveRules.summary.maintainPctOfTier',
        '{{pct}}% maintain per tier',
        { pct: ruleValue?.value ?? '—' }
      );
    case 'upgrade_count_per_tier': {
      const count = Object.keys(ruleValue?.per_tier || {}).length;
      return t(
        'admin.objectiveRules.summary.upgradeCount',
        'Upgrade count ({{count}} tiers)',
        { count }
      );
    }
    case 'maintain_count_per_tier': {
      const count = Object.keys(ruleValue?.per_tier || {}).length;
      return t(
        'admin.objectiveRules.summary.maintainCount',
        'Maintain count ({{count}} tiers)',
        { count }
      );
    }
    default:
      return ruleType;
  }
};

const RuleTypeChip = ({ ruleType }) => {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-accent/10 text-accent border border-accent/20 font-data">
      {ruleType}
    </span>
  );
};

const TenantRulesSection = ({ tenantId, canWrite }) => {
  const { t } = useTranslation();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    const { data, error: dbError } = await supabase
      .from('tenant_objective_rules')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('metric', { ascending: true });
    if (dbError) setError(dbError.message);
    setRules(data || []);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const takenMetrics = useMemo(() => rules.map((r) => r.metric), [rules]);

  const handleAdd = () => {
    setEditingRule(null);
    setEditorOpen(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setEditorOpen(true);
  };

  const handleDelete = async (rule) => {
    if (
      !window.confirm(
        t(
          'admin.objectiveRules.tenant.confirmDelete',
          'Delete this rule? Targets generated from it will remain until you regenerate.'
        )
      )
    )
      return;
    const { error: dbError } = await supabase
      .from('tenant_objective_rules')
      .delete()
      .eq('id', rule.id);
    if (dbError) {
      setError(dbError.message);
      return;
    }
    await load();
  };

  const handleSave = useCallback(
    async ({ metric, rule_type, rule_value }) => {
      if (!tenantId) return;
      // UPSERT on (tenant_id, metric)
      const payload = {
        tenant_id: tenantId,
        metric,
        rule_type,
        rule_value,
      };
      const { error: dbError } = await supabase
        .from('tenant_objective_rules')
        .upsert(payload, { onConflict: 'tenant_id,metric' });
      if (dbError) throw dbError;
      await load();
    },
    [tenantId, load]
  );

  return (
    <section className="space-y-3">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="font-heading text-base md:text-lg font-semibold text-foreground">
            {t('admin.objectiveRules.tenant.title', 'Tenant rules')}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t(
              'admin.objectiveRules.tenant.subtitle',
              'Default rules applied to every agent under this tenant when no agency override exists.'
            )}
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={handleAdd}
            className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
          >
            <span className="text-base leading-none">+</span>
            {t('admin.objectiveRules.tenant.addRule', 'Add rule')}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-error/30 bg-error/5 px-4 py-2 text-sm text-error">
          {error}
        </div>
      )}

      <div className="rounded-2xl bg-card border border-border overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-12 rounded skeleton" />
            ))}
          </div>
        ) : rules.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            {t(
              'admin.objectiveRules.tenant.empty',
              'No tenant rules yet. Add one to define how monthly targets are computed.'
            )}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rules.map((r) => (
              <li
                key={r.id}
                className="px-4 py-3 flex flex-col md:flex-row md:items-center gap-3 hover:bg-surface-2/40 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-data text-sm text-foreground font-semibold">
                      {r.metric}
                    </span>
                    <RuleTypeChip ruleType={r.rule_type} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 tabular-nums">
                    {formatRuleSummary(r.rule_type, r.rule_value, t)}
                  </div>
                </div>
                {canWrite && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleEdit(r)}
                      className="px-3 py-1.5 rounded-lg bg-card border border-border text-foreground text-xs font-medium hover:bg-muted transition-colors"
                    >
                      {t('admin.objectiveRules.tenant.edit', 'Edit')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(r)}
                      className="px-2 py-1.5 rounded-lg text-error hover:bg-error/10 transition-colors text-xs"
                      aria-label={t('admin.objectiveRules.tenant.delete', 'Delete')}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <RuleEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
        initialRule={editingRule}
        defaultMetrics={DEFAULT_METRICS}
        takenMetrics={takenMetrics}
        scope="tenant"
      />
    </section>
  );
};

export default TenantRulesSection;
